import { Button, Col, Divider, Progress, Row, Statistic } from 'antd';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import assignmentApi from '../../../../api/AssignmentApi';
import { AssignmentSkillStats, AssignmentWithSkillStats } from '../../../../model/entities';
import styles from './AssignmentSkillsComponent.module.scss';

/**
 * This component represents an assignment skill component.
 * @param props - the props
 */
export const AssignmentSkillsComponent: React.FC<Props> = (props) => {
    const { assignmentId } = props;

    /*** HOOKS ***/

    const [assignmentWithSkillStats, setAssignmentWithSkillStats] = useState<AssignmentWithSkillStats>();
    const [visible, setVisible] = useState<boolean>(false);

    useEffect(() => {
        const init = async () => {
            try {
                const assignmentWithSkillStats = await assignmentApi.getStats(assignmentId);
                setAssignmentWithSkillStats(assignmentWithSkillStats);
            } catch (error) {
                // no error message
            }
        };
        init();
    }, [assignmentId]);

    /*** METHODS ***/

    const togglePanel = (event: any) => {
        event.preventDefault();
        setVisible(!visible);
    };

    /*** VISUAL ***/

    return (
        <>
            <Row gutter={[16, 16]} className={styles.buttons}>
                <Col span={24}>
                    <Button
                        type="link"
                        className={styles.link}
                        onClick={togglePanel}
                        disabled={assignmentWithSkillStats?.skills.length === 0}
                    >
                        <FormattedMessage id="button.seeAll" />
                    </Button>
                </Col>
            </Row>

            <Row gutter={[16, 16]} hidden={!visible} className={styles.container}>
                <Divider />

                <Col span={24}>
                    <h4>
                        <FormattedMessage id="admin.dashboard.assignments.skills.profile" />
                    </h4>
                    <p>{assignmentWithSkillStats?.profileName}</p>
                </Col>

                {assignmentWithSkillStats?.skills.map((skill) => (
                    <Col span={24}>
                        <Statistic
                            title={
                                <>
                                    <h4>
                                        <FormattedMessage id="admin.dashboard.assignments.skills.skill" />
                                    </h4>
                                    <p>{skill.skillName}</p>
                                </>
                            }
                            valueRender={() => (
                                <Row gutter={28}>
                                    <Col span={12}>
                                        <Progress
                                            strokeLinecap="square"
                                            type="circle"
                                            percent={getAssignmentFinished(skill)}
                                            success={{ percent: getAssignmentPassed(skill), strokeColor: '#00154b' }}
                                            format={() => (
                                                <>
                                                    <FormattedNumber
                                                        value={getAssignmentPassed(skill)}
                                                        maximumFractionDigits={1}
                                                    />
                                                    %
                                                </>
                                            )}
                                            className={styles.results}
                                        />
                                    </Col>
                                    <Col span={12} className={styles.legend}>
                                        <p className={styles.passed}>
                                            <span />
                                            <FormattedMessage id="admin.dashboard.assignments.results.passed" />
                                        </p>
                                        <p className={styles.failed}>
                                            <span />
                                            <FormattedMessage id="admin.dashboard.assignments.results.failed" />
                                        </p>
                                    </Col>
                                </Row>
                            )}
                        ></Statistic>
                    </Col>
                ))}
            </Row>
        </>
    );
};
export default AssignmentSkillsComponent;

interface Props {
    assignmentId: number;
}

const getAssignmentPassed = (assignment: AssignmentSkillStats): number => {
    const total = assignment.passedNumber + assignment.failedNumber;
    return total === 0 ? 0 : (assignment.passedNumber / total) * 100;
};

const getAssignmentFailed = (assignment: AssignmentSkillStats): number => {
    const total = assignment.passedNumber + assignment.failedNumber;
    return total === 0 ? 0 : (assignment.failedNumber / total) * 100;
};

const getAssignmentFinished = (assignment: AssignmentSkillStats): number => {
    return getAssignmentPassed(assignment) + getAssignmentFailed(assignment);
};
